import React, { useEffect, useState } from "react";
import { EyeOutlined, PlusOutlined, DeleteOutlined, CloseOutlined, CheckOutlined, PlusSquareFilled } from '@ant-design/icons';
import { DatePicker, Modal, Col, Row, Table, Space, Button, Form, Select, Input } from 'antd';
import type { AlignType } from 'rc-table/lib/interface';
import dayjs from "dayjs";

import { SelecionarArquivo } from "./Documentos/SelecionarArquivos";
import { errorMessage, successMessage } from "../messages/messages";
import type { ICtrlDoc, IDocumento, ISelectList } from "./Interfaces";
import type { IChaves } from "./Interfaces";

import { disabledDate } from "../utils/utilsFunctions";
import { getDocPdf } from "../services/apiDocumentos";
import { hasPermissao, Papel } from "./auth/entidadeUsuario";

interface IProps {
  ctrlDoc: ICtrlDoc[];
  objetoDocumento: IDocumento[];
  setObjetoDocumento: React.Dispatch<React.SetStateAction<IDocumento[]>>;
  listaDocumentos: ISelectList[];
  manter?: number; // 1 : Inserir / 2 : Consultar / 3 : Editar;
}

export interface ICampos {
  visible?: string[];
  default?: string[];
  defaultNumberSelect?: number[];
  required?: number[];
  disabled?: number[];
}

const FormModalDocumento = ({ ctrlDoc, objetoDocumento, setObjetoDocumento, listaDocumentos, manter }: IProps) => {
  const [form] = Form.useForm();

  const [chave, setChave] = useState<number | undefined>();
  const [fileList, setFileList] = useState<IChaves>();
  const [idCtrlDocTpDoc, setIdCtrlDocTpDoc] = useState(0);

  const [exibirModalDoc, setExibirModalDoc] = useState(false);
  const [exibirDocCadastrado, setExibirDocCadastrado] = useState("none");
  const [exibirElementoAcaoAlteracao, setExibirElementoAcaoAlteracao] = useState("inline");

  const [numeroDocObrigatorio, setNumeroDocObrigatorio] = useState(false);
  const [nomeDocObrigatorio, setNomeDocObrigatorio] = useState(false);
  const [dataEmissaoObrigatoria, setDataEmissaoObrigatoria] = useState(false);
  const [_dataVencimentoObrigatoria, setDataVencimentoObrigatoria] = useState(false);
  const [_emissorObrigatorio, setEmissorObrigatorio] = useState(false);
  const [_ufObrigatorio, setUfObrigatorio] = useState(false);
  const [_paisObrigatorio, setPaisObrigatorio] = useState(false);

  useEffect(() => {
    if (idCtrlDocTpDoc) {
      applyCtrlDocRules(idCtrlDocTpDoc);
    }
  }, [idCtrlDocTpDoc]);


  useEffect(() => {
    if (objetoDocumento.length === 0) {
      setExibirDocCadastrado("none")
    } else {
      setExibirDocCadastrado("inline")
    }
  }, [objetoDocumento]);

  useEffect(() => {
    if (chave) {
      setFileList(
        {
          idDocumento: chave,
          ativo: true,
        },
      );
    }
  }, [chave]);


  useEffect(() => {
    if (manter === 2 || manter === 9) { // Detalhar
      setExibirElementoAcaoAlteracao("none");
    } else {
      setExibirElementoAcaoAlteracao("inline");
    }
  }, [manter]);

  function applyCtrlDocRules(id: number) {
    const item = ctrlDoc.find(c => c.idCtrlDocTpDoc === id);

    if (!item) return;

    setNumeroDocObrigatorio(item.temNumero);
    setNomeDocObrigatorio(item.temNome);
    setDataEmissaoObrigatoria(item.temDataEmissao);
    setDataVencimentoObrigatoria(item.temDataVencimento);
    setEmissorObrigatorio(item.temEmissor);
    setUfObrigatorio(item.temUf);
    setPaisObrigatorio(item.temPais);
  }

  const controleDaGrid = (botao: string) => {
    if (botao === "cancelar") {
      setExibirModalDoc(false);
      setExibirElementoAcaoAlteracao("inline");
    } else if (botao === "incluir") {
      setExibirModalDoc(false);
      setExibirElementoAcaoAlteracao("inline");
    } else {
      setExibirModalDoc(true);
      setExibirElementoAcaoAlteracao("none");
    }
    if (manter === 2) { // Detalhar
      setExibirElementoAcaoAlteracao("none");
    }
  };

  const validarDoc = () => {
    if (!fileList || !fileList.idDocumento) {
      errorMessage("Selecione um arquivo para 'upload'.");
    } else {
      form
        .validateFields([
          "idDocumento",
          "nmDocumento",
          "nuDocumento",
          "nmTpDoc",
          "dtDocumento",
        ])
        .then(() => {
          controleDaGrid("incluir");
          setDadosDocumento();
          successMessage("Documento Adicionado");
        })
        .catch((_error) => {
          errorMessage("Campo obrigatório não preechido ou não validado");
        });
    }
  };

  function setDadosDocumento() {
    if (!chave) {
      errorMessage("O identificador do documento no serviço de documentos não foi preenchido");
      return
    }

    if (idCtrlDocTpDoc === 0) {
      errorMessage("O tipo de documento não foi identificado.");
      return
    }

    setObjetoDocumento([
      ...objetoDocumento,
      {
        idDocCad: -1,
        nuDocumento: form?.getFieldValue("nuDocumento") ? form?.getFieldValue("nuDocumento").toUpperCase() : undefined,
        dtDocumento: form?.getFieldValue("dtDocumento") ? dayjs(form?.getFieldValue("dtDocumento")).format("YYYY-MM-DD") : "",
        idCtrlDocTpDoc: idCtrlDocTpDoc,
        nmDocumento: form?.getFieldValue("nmDocumento")?.toUpperCase() || "",
        idDocumento: chave,
        documentoChave: fileList,
        marcadoParaExclusao: false,
      },
    ]);
    form.resetFields();
    setIdCtrlDocTpDoc(0);
    setChave(undefined);
    setFileList(undefined);

    setNumeroDocObrigatorio(false);
    setNomeDocObrigatorio(false);
    setDataEmissaoObrigatoria(false);
    setDataVencimentoObrigatoria(false);
    setEmissorObrigatorio(false);
    setUfObrigatorio(false);
    setPaisObrigatorio(false);

  }

  const columns = [
    {
      key: "1",
      title: "Tipo",
      dataIndex: "",
      render: (IDoc: IDocumento) => (
        <>
          {listaDocumentos.find(l => l.value === IDoc.idCtrlDocTpDoc)?.label}
        </>
      ),
    },
    {
      key: "2",
      title: "Número",
      dataIndex: "nuDocumento",
    },
    {
      key: "3",
      title: "Nome",
      dataIndex: "nmDocumento",
    },
    {
      key: "4",
      title: "Data",
      dataIndex: "",
      render: (IDoc: IDocumento) => (
        <>
          {IDoc.dtDocumento ? dayjs(IDoc.dtDocumento).format("DD/MM/YYYY") : ""}
        </>
      ),
    },
    {
      key: "111",
      title: "Arquivo",
      dataIndex: "",
      align: 'center' as AlignType,
      render: (IDoc: IDocumento) => (
        <Button
          id="arquivo"
          shape="round"
          icon={<EyeOutlined />}
          htmlType="button"
          onClick={() => {
            getDocPdf(IDoc.idDocumento!);
          }}
        />
      ),
    },
    {
      // Apenas Marca ou desmarca o campo ativo. Não existe a opção de alteração de documento. Somente exclusão e inclusão de um novo.
      key: "9",
      title: "Opções",
      dataIndex: "",
      align: 'center' as AlignType,
      render: (IDoc: IDocumento) => (
        <>
          <Space>
            <div style={{ display: manter === 1 ? "inline" : exibirElementoAcaoAlteracao }}>
              <DeleteOutlined
                title="Excluir documento ainda não gravado na base."
                style={{ color: "red", display: (IDoc?.idDocCad && IDoc?.idDocCad < 0) ? "inline" : "none" }} // EXCLUSÃO DE REGISTRO SENDO INSERIDO NO PROCESSO DE ALTERAÇÃO. OU SEJA. QUE AINDA NÃO ESTÁ NA BASE
                onClick={() => {
                  setObjetoDocumento(objetoDocumento.filter((doc) => doc !== IDoc));
                }}
              />
            </div>

            <div style={{ display: (IDoc?.idDocCad && IDoc?.idDocCad > 0) && hasPermissao([Papel.DILIC_MASTER]) ? exibirElementoAcaoAlteracao : "none" }}>
              <>
                <PlusSquareFilled
                  title="Desmarcar para exclução"
                  style={{ color: "blue", display: (IDoc.marcadoParaExclusao) ? "inline" : "none" }}
                  onClick={() => {
                    IDoc.marcadoParaExclusao = !IDoc.marcadoParaExclusao; // INVERTE ENTRE 1 (REGISTRO ATUAL ATIVO) E 2 (REGISTRO ATUAL DELETADO)
                    setObjetoDocumento(pre => { //TROCA O REGISTRO DO DATASET PELO REGISTRO EM ALTERAÇÃO
                      return pre.map(doc => {
                        if (doc.idDocCad === IDoc.idDocCad) {
                          return IDoc;
                        } else {
                          return doc;
                        }
                      });
                    });

                  }}
                />
              </>

              <>
                <DeleteOutlined
                  title="Marcar para exclução/inativação"
                  style={{ color: "red", display: (!IDoc.marcadoParaExclusao) ? "inline" : "none" }}
                  onClick={() => {
                    IDoc.marcadoParaExclusao = !IDoc.marcadoParaExclusao; // INVERTE ENTRE 1 (REGISTRO ATUAL ATIVO) E 2 (REGISTRO ATUAL DELETADO)
                    setObjetoDocumento(pre => { //TROCA O REGISTRO DO DATASET PELO REGISTRO EM ALTERAÇÃO
                      return pre.map(doc => {
                        if (doc.idDocCad === IDoc.idDocCad) {
                          return IDoc;
                        } else {
                          return doc;
                        }
                      });
                    });

                  }}
                />
              </>
            </div>
          </Space>
        </>
      ),
    },

  ];

  const handlerTipoDocumento = async (tpDoc: any) => {
    setIdCtrlDocTpDoc(tpDoc.value);
    form.setFieldsValue({
      "nmTpDoc": tpDoc.label,
    })
  };

  return (
    <Form layout='vertical' style={{ padding: 24 }} scrollToFirstError form={form} >

      <Button
        style={{ display: exibirElementoAcaoAlteracao }}
        shape='round'
        onClick={() => {
          controleDaGrid("incluir documento");
        }}
        type='primary'
        icon={<PlusOutlined />}
      >
        Incluir Documento
      </Button>
      <>

        <Modal title="Incluir Documento" open={exibirModalDoc}
          style={{ top: 20 }}
          // width={750}
          destroyOnHidden={true}
          onCancel={() => {
            controleDaGrid("cancelar");
            setIdCtrlDocTpDoc(0);
            form.resetFields([
              "idMandatoDirigenteDocumento",
              "nuDocumento",
              "nmTpDoc",
              "dtDocumento",
            ]);
          }}
          closable={false}
          cancelText="Cancelar"
          cancelButtonProps={{ shape: "round", icon: <CloseOutlined /> }}

          onOk={() => {
            validarDoc();
          }}
          okText="Incluir"
          okButtonProps={{ disabled: !chave, shape: "round", icon: <CheckOutlined /> }}
        >

          <Row>
            <Col xs={24} sm={20}>
              <Form.Item label="Tipo de Documento"
                labelAlign="left"
                labelCol={{ span: 20 }}
                name="nmTpDoc"
                colon={false}
                rules={[
                  {
                    required: true,
                    message: "O campo tipo de documento é obrigatório",
                  },
                ]}
              >
                <Select
                  showSearch
                  filterOption
                  optionFilterProp='children'
                  placeholder='Tipo Documento'
                  options={listaDocumentos}
                  onChange={(_, index) => {
                    handlerTipoDocumento(index);
                  }}
                // disabled={estadoModal === 2}
                />

              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col xs={24} sm={8}>
              <Form.Item label="Número"
                name="nuDocumento"
                colon={false}
                rules={[
                  {
                    required: (numeroDocObrigatorio),
                    message: "O campo número do documento é obrigatório",
                  },
                ]}
              >
                <Input
                  placeholder="Número"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={8}>
              <Form.Item label="Data"
                name="dtDocumento"
                colon={false}
                rules={[
                  {
                    required: (dataEmissaoObrigatoria),
                    message: "O campo data do documento é obrigatório",
                  },
                ]}
              >
                <DatePicker
                  format={"DD/MM/YYYY"}
                  style={{ width: "100%" }}
                  disabledDate={disabledDate}
                  placeholder="Selecionar Data"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row>
            <Col xs={24} sm={16}>
              <Form.Item label="Nome documento"
                name="nmDocumento"
                colon={false}
                rules={[
                  {
                    required: (nomeDocObrigatorio),
                    message: "Informe o nome do documento",
                  },
                ]}
              >
                <Input
                  disabled={idCtrlDocTpDoc !== 1}
                  placeholder="Outro Documento" />
              </Form.Item>
            </Col>
          </Row>

          <Row justify="space-between">
            <Col xs={24} md={12} xl={5}>
              <SelecionarArquivo
                setChave={setChave}
                identificador={true}
                label="Arquivo"
              />
            </Col>
          </Row>

        </Modal>

      </>

      <Row style={{ display: exibirDocCadastrado }}>
        <Col
          xs={24}
          sm={24}
        >
          <Table
            size="small" rowKey="idDocumento" columns={columns} dataSource={objetoDocumento} pagination={false} />
        </Col>
      </Row>
    </Form>
  )
};

export default FormModalDocumento;
